import { log } from '@/lib/utils/logger';
import { reportApiContractDeviation } from '@/lib/utils/reportApiContractDeviation';

export type IAuthContext = {
  getCsrfToken?: () => string | undefined;
  getAuthToken?: () => string | undefined;
};

export function getCsrfToken(req?: Request): string | undefined {
  const extractToken = (cookieHeader: string | null | undefined): string | undefined => {
    const tokenEntry = cookieHeader?.split('; ').find((row) => row.startsWith('CSRF-TOKEN='));

    if (!tokenEntry) {
      return undefined;
    }

    // Keep full value after the first '=' to preserve base64 padding.
    const token = tokenEntry.slice('CSRF-TOKEN='.length);
    return decodeURIComponent(token);
  };

  if (typeof window !== 'undefined') {
    // Client-side (CSR)
    return extractToken(document.cookie);
  } else if (req) {
    // Server-side (SSR/API)
    return extractToken(req.headers.get('cookie'));
  }
  return undefined;
}

export const CsrfTokenProvider = {
  getCsrfToken: () => getCsrfToken(),
};

/**
 * Per-page prepublish/DOI key sent as X-LiST-Data-Package so child resource calls
 * (snapshots, activities, files) can present the same secret as the data-package route.
 * Absent means not set — never send an empty string.
 */
let dataPackageKey: string | null = null;

export const DataPackageKeyProvider = {
  HeaderName: 'X-LiST-Data-Package',
  setKey(key: string | null | undefined) {
    dataPackageKey = key && key.length > 0 ? key : null;
  },
  getKey(): string | null {
    return dataPackageKey;
  },
  clear() {
    dataPackageKey = null;
  },
};

/**
 * Refreshes the CSRF token by fetching it from the server
 * @param baseUrl - The base URL of the API endpoint
 * @returns Promise that resolves when the token is refreshed
 */
export async function refreshCsrfToken(baseUrl: string): Promise<void> {
  try {
    const csrfUrl = `${baseUrl}/auth/csrf`;
    await fetch(csrfUrl, {
      credentials: 'include', // Ensure cookies are included
    });
    log.debug('[AuthorizedApiBase] CSRF token refreshed');
  } catch (error) {
    log.error('[AuthorizedApiBase] Failed to refresh CSRF token:', error);
    throw error;
  }
}

/** True when the body looks like RFC 7807 ProblemDetails (our API error contract). */
function isProblemDetailsShape(parsed: unknown): boolean {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return false;
  }
  const o = parsed as Record<string, unknown>;
  // Prefer detail; title+status is the shell ASP.NET returns for status-only client errors.
  return typeof o.detail === 'string' || (typeof o.status === 'number' && typeof o.title === 'string');
}

/**
 * Best-effort ProblemDetails.detail from a thrown ApiException-like object.
 * Kept local to avoid importing apiErrors (ApiClient → AuthorizedApiBase cycle).
 */
function readThrownApiDetail(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  const result = (err as { result?: unknown }).result;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const detail = (result as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
  }
  return undefined;
}

/**
 * Base class for API clients requiring authorization
 */
export class AuthorizedApiBase {
  private readonly _authContext: IAuthContext;
  private _expiration: string | null = null;

  public get tokenExpiration() {
    return this._expiration;
  }

  protected constructor(config: IAuthContext) {
    this._authContext = config;
  }

  // Unfortunately, we do need to return any here -> that's what the generator expects.
  protected transformResult(_url: string, response: Response, processor: (response: Response) => any) {
    this._expiration = response.headers.get('x-token-expiration');

    const header = (name: string) => response.headers.get(name) ?? response.headers.get(name.toLowerCase());
    const supportInfo = {
      url: _url,
      status: response.status,
      statusText: response.statusText,
      tokenExpiration: this._expiration,
      requestId: header('x-request-id') ?? header('x-correlation-id') ?? header('request-id') ?? undefined,
      traceparent: header('traceparent') ?? undefined,
      date: header('date') ?? undefined,
      timestamp: new Date().toISOString(),
    };

    const attachSupportInfo = (err: unknown) => {
      if (!err || (typeof err !== 'object' && typeof err !== 'function')) return err;

      const e = err as Record<string, unknown>;
      if (!('supportInfo' in e)) {
        try {
          e.supportInfo = supportInfo;
        } catch {
          // ignore if non-writable/sealed
        }
      }

      // If this looks like NSwag's ApiException, also include the supportInfo in `result`
      // since some UI layers display that instead of the raw response string.
      try {
        const isApiExceptionLike =
          typeof e.status === 'number' && typeof e.response === 'string' && typeof e.message === 'string';
        if (isApiExceptionLike && e.result == null) {
          e.result = { supportInfo };
        } else if (isApiExceptionLike && typeof e.result === 'object' && e.result) {
          (e.result as Record<string, unknown>).supportInfo = supportInfo;
        }
      } catch {
        // ignore best-effort enrichment
      }

      return err;
    };

    /**
     * Enrich and rethrow. Do not log.error expected business 4xx (409/424/428/…) with the Error
     * object — console.error dumps a stack for every rejection. UI catch sites own those.
     * 400 is often a client bug (bad payload the UI should have validated) → warn.
     * 5xx and unexpected ok-path processor failures → error (compact fields only).
     */
    const logAndRethrow = (label: string, err: unknown): never => {
      attachSupportInfo(err);
      const status = supportInfo.status;
      const payload = {
        status,
        url: supportInfo.url,
        detail: readThrownApiDetail(err),
        requestId: supportInfo.requestId,
      };
      const message = `[AuthorizedApiBase] API request failed (${label})`;
      if (label === 'ok' || status >= 500) {
        log.error(message, payload);
      } else if (status === 400) {
        log.warn(message, payload);
      } else {
        log.debug(message, payload);
      }
      throw err;
    };

    const withSupportHandling = (label: string, run: () => any) => {
      try {
        const out = run();
        if (out && typeof out === 'object' && 'then' in out && typeof (out as { then?: unknown }).then === 'function') {
          return (out as Promise<unknown>).catch((err) => logAndRethrow(label, err));
        }
        return out;
      } catch (err) {
        return logAndRethrow(label, err);
      }
    };

    if (response.ok) {
      return withSupportHandling('ok', () => processor(response));
    }

    return (async () => {
      // Trigger session recovery for every 401 automatically, so individual components never
      // need to check for it in their own catch blocks. Dynamic import avoids a circular
      // dependency: authStore → ApiClient → AuthorizedApiBase.
      if (response.status === 401) {
        const { useAuthStore } = await import('@/stores/authStore');
        const { sessionLostDialogOpen, attemptSessionRecovery } = useAuthStore.getState();
        if (!sessionLostDialogOpen) {
          void attemptSessionRecovery();
        }
      }

      let responseText: string | undefined;
      try {
        responseText = await response.clone().text();
      } catch (readErr) {
        reportApiContractDeviation('Failed to read error response body; passing response through.', {
          url: _url,
          status: response.status,
          readErr,
        });
        return withSupportHandling('non-ok (read body failed)', () => processor(response));
      }

      // Empty body is allowed (status-only return NotFound(), etc.) — not a contract violation.
      if (!responseText) {
        return withSupportHandling('non-ok (empty body)', () => processor(response));
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(responseText) as unknown;
      } catch {
        reportApiContractDeviation('Error response body is not JSON; passing through as-is.', {
          url: _url,
          status: response.status,
          bodyPreview: responseText.slice(0, 200),
        });
        return withSupportHandling('non-ok (non-JSON body)', () => processor(response));
      }

      if (isProblemDetailsShape(parsed)) {
        return withSupportHandling('non-ok (ProblemDetails)', () => processor(response));
      }

      // Legacy / unexpected shapes — report, then best-effort recovery.
      const legacyMessage =
        parsed &&
        typeof parsed === 'object' &&
        'message' in parsed &&
        typeof (parsed as { message?: unknown }).message === 'string'
          ? (parsed as { message: string }).message
          : undefined;

      if (legacyMessage !== undefined) {
        reportApiContractDeviation(
          'Error body uses legacy { message } instead of ProblemDetails.detail; normalizing for NSwag.',
          { url: _url, status: response.status, message: legacyMessage }
        );

        const wrappedResponse = new Proxy(response as unknown as Record<string, unknown>, {
          get(target, prop, receiver) {
            if (prop === 'text') {
              return () => Promise.resolve(legacyMessage);
            }
            if (prop === 'json') {
              return () => Promise.resolve({ message: legacyMessage });
            }

            const value = Reflect.get(target, prop, receiver) as unknown;
            return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value;
          },
        });

        return withSupportHandling('non-ok (normalized legacy message)', () =>
          processor(wrappedResponse as unknown as Response)
        );
      }

      reportApiContractDeviation('Error body is JSON but not ProblemDetails; passing through as-is.', {
        url: _url,
        status: response.status,
        bodyPreview: responseText.slice(0, 200),
      });
      return withSupportHandling('non-ok (unexpected JSON)', () => processor(response));
    })();
  }

  /**
   * Adds authorization header to request options
   */
  protected transformOptions = async (options: RequestInit): Promise<RequestInit> => {
    try {
      const csrfToken = this._authContext.getCsrfToken?.() ?? '';
      const authToken = this._authContext.getAuthToken?.() ?? '';
      const dataPackageKey = DataPackageKeyProvider.getKey();

      const transformedOptions = {
        ...options,
        headers: {
          ...options.headers,
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
          ...(dataPackageKey ? { [DataPackageKeyProvider.HeaderName]: dataPackageKey } : {}),
        },
      };

      return transformedOptions;
    } catch (error) {
      log.error('[AuthorizedApiBase] Error transforming request options:', error);
      throw error;
    }
  };
}
